import React, { useState, useEffect } from 'react';
import { Dropdown, Button, Modal, Form, Alert, Spinner, Badge, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faPlus, faCrown, faUserShield, faUsers, faCog, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { Auth, API, Storage } from 'aws-amplify';
import { useOrganization } from '../../contexts/OrganizationContext';
import * as queries from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { createDefaultEmailTemplates } from '../../utils/emailTemplates';
import { syncUserWithDatabase } from '../../utils/userSync';
import { ensureUserReadyForOrganization, waitForPropagation } from '../../utils/userSyncCheckpoint';
import { createOrganizationForUser } from '../../utils/createOrganization';

// Add custom styles to hide default dropdown arrow
const dropdownStyles = `
  .custom-dropdown-toggle::after {
    display: none !important;
  }
`;

// Module-level guard so the first-time-user readiness checkpoint runs at most
// once per page load. This survives component remounts (the whole AppRouter
// subtree can be unmounted/remounted by TermsAndConditionsCheck while auth
// state settles), which previously caused the checkpoint to restart forever and
// flip between the loading/verifying spinners. Resets naturally on a full reload.
let firstTimeCheckpointStarted = false;

const OrganizationSelector = () => {
  const navigate = useNavigate();
  const { activeOrganization, setActiveOrganization } = useOrganization();
  const [organizations, setOrganizations] = useState({
    owned: [],
    coOwned: [],
    member: []
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  // P-07: whether an OrganizationMember row exists for this user's email (a
  // pending/existing invite), and whether they chose "I'm waiting for an invitation".
  const [invitePending, setInvitePending] = useState(false);
  const [waitingForInvite, setWaitingForInvite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [user, setUser] = useState(null);
  const [logoUrls, setLogoUrls] = useState({});
  const [activeOrgLogo, setActiveOrgLogo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [creatingOrganization, setCreatingOrganization] = useState(false);

  const hasNoOrganizations = () => {
    return (
      !loading &&
      organizations.owned.length === 0 &&
      organizations.coOwned.length === 0 &&
      organizations.member.length === 0
    );
  };

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        console.log('Current authenticated user:', currentUser);
        setUser(currentUser);
        setUserEmail(currentUser.attributes.email);
      } catch (error) {
        console.error('Error getting current user:', error);
        setLoading(false);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (userEmail) {
      fetchOrganizations();
    }
  }, [userEmail]);

  useEffect(() => {
    if (!hasNoOrganizations()) {
      return;
    }

    // P-07: don't force org creation on an invited user (an OrganizationMember
    // row exists for their email) or on someone who chose to wait for an invite.
    if (invitePending || waitingForInvite) {
      setShowCreateModal(false);
      return;
    }

    // Show the "create your first organization" modal immediately. This is a
    // pure function of current state, so it re-applies correctly even if the
    // component is remounted (e.g. by TermsAndConditionsCheck) — it never gets
    // stuck on a spinner waiting for an async checkpoint that lands on an
    // already-unmounted instance.
    if (!showCreateModal) {
      setShowCreateModal(true);
    }

    // Warm up the user-readiness checkpoint once per page load, in the
    // background. It must NOT block/gate the UI: createOrganization() runs the
    // authoritative checkpoint again before it actually creates anything, so
    // this is best-effort. Blocking here previously caused a remount spinner loop.
    if (!firstTimeCheckpointStarted) {
      firstTimeCheckpointStarted = true;
      console.log('🔍 First time user detected, warming up organization readiness checkpoint...');
      ensureUserReadyForOrganization(5, 2000)
        .then((readinessResult) => {
          if (!readinessResult.success) {
            console.warn('🚨 User readiness checkpoint did not pass:', readinessResult.message);
          } else {
            console.log('✅ User checkpoint passed - ready for organization creation');
          }
        })
        .catch((error) => {
          console.error('❌ Error in organization readiness checkpoint:', error);
        });
    }
  }, [loading, organizations, showCreateModal, invitePending, waitingForInvite]);

  useEffect(() => {
    if (activeOrganization?.logo) {
      fetchLogoUrl(activeOrganization.logo)
        .then(url => setActiveOrgLogo(url))
        .catch(err => console.error('Error fetching active org logo:', err));
    } else {
      setActiveOrgLogo(null);
    }
  }, [activeOrganization]);

  useEffect(() => {
    // Fetch logos for all organizations
    const fetchAllLogos = async () => {
      const allOrgs = [...organizations.owned, ...organizations.coOwned, ...organizations.member];
      const logos = {};
      
      for (const org of allOrgs) {
        if (org.logo) {
          try {
            const url = await fetchLogoUrl(org.logo);
            logos[org.id] = url;
          } catch (error) {
            console.error(`Error fetching logo for org ${org.id}:`, error);
          }
        }
      }
      
      setLogoUrls(logos);
    };
    
    if (!loading) {
      fetchAllLogos();
    }
  }, [organizations, loading]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const user = await Auth.currentAuthenticatedUser();
      const userEmail = user.attributes.email;
      const userSub = user.attributes.sub;
      
      console.log('Current user:', { 
        email: userEmail,
        sub: userSub
      });

      // Get ALL organizations (paginated to catch all owned/coOwned orgs)
      let allOrgItems = [];
      let orgsNextToken = null;
      do {
        const orgsPage = await API.graphql({
          query: queries.listOrganizations,
          variables: { limit: 100, nextToken: orgsNextToken }
        });
        allOrgItems = [...allOrgItems, ...orgsPage.data.listOrganizations.items];
        orgsNextToken = orgsPage.data.listOrganizations.nextToken;
      } while (orgsNextToken);

      // Get member organizations (paginated, filter by userSub for reliability across email changes)
      let allMemberItems = [];
      let membersNextToken = null;
      do {
        const membersPage = await API.graphql({
          query: queries.listOrganizationMembers,
          variables: {
            filter: { userSub: { eq: userSub } },
            limit: 100,
            nextToken: membersNextToken
          }
        });
        allMemberItems = [...allMemberItems, ...membersPage.data.listOrganizationMembers.items];
        membersNextToken = membersPage.data.listOrganizationMembers.nextToken;
      } while (membersNextToken);

      // P-07: pending invitations are keyed by EMAIL with an empty userSub until
      // the invitee first logs in, so the userSub filter above misses them.
      // Detect them by email so an invited user isn't forced to create an org.
      let allEmailMemberItems = [];
      let emailMembersNextToken = null;
      do {
        const emailMembersPage = await API.graphql({
          query: queries.listOrganizationMembers,
          variables: {
            filter: { email: { eq: userEmail } },
            limit: 100,
            nextToken: emailMembersNextToken
          }
        });
        allEmailMemberItems = [...allEmailMemberItems, ...emailMembersPage.data.listOrganizationMembers.items];
        emailMembersNextToken = emailMembersPage.data.listOrganizationMembers.nextToken;
      } while (emailMembersNextToken);
      setInvitePending(allEmailMemberItems.some(member => !member._deleted));

      const memberOrgIds = allMemberItems
        .filter(member => !member._deleted && member.status === 'ACTIVE')
        .map(member => member.organizationID);

      // Filter organizations
      const allOrgs = allOrgItems.filter(org => !org._deleted);
      
      console.log('Processing organizations:', allOrgs.map(org => ({
        name: org.name,
        owner: org.owner,
        additionalOwners: org.additionalOwners,
        isOwner: org.owner === userSub,
        isCoOwner: Array.isArray(org.additionalOwners) && org.additionalOwners.includes(userEmail),
        isMember: memberOrgIds.includes(org.id)
      })));

      // First identify owned and co-owned
      const owned = [];
      const coOwned = [];
      const member = [];

      allOrgs.forEach(org => {
        if (org.owner === userSub) {
          owned.push(org);
        } else if (Array.isArray(org.additionalOwners) && org.additionalOwners.includes(userEmail)) {
          coOwned.push(org);
        } else if (memberOrgIds.includes(org.id)) {
          member.push(org);
        }
      });

      console.log('Final categorization:', {
        owned: owned.map(o => o.name),
        coOwned: coOwned.map(o => o.name),
        member: member.map(o => o.name)
      });

      setOrganizations({ owned, coOwned, member });
      
      // Check for newly created organization ID in localStorage
      const newOrgId = localStorage.getItem('newlyCreatedOrgId');
      if (newOrgId) {
        // Find the organization in any category
        const newOrg = [...owned, ...coOwned, ...member].find(org => org.id === newOrgId);
        if (newOrg) {
          setActiveOrganization(newOrg);
          // Clear the stored ID after setting it
          localStorage.removeItem('newlyCreatedOrgId');
          return;
        }
      }
      
      // If no newly created org or it wasn't found, set default active org
      if (!activeOrganization && (owned.length > 0 || coOwned.length > 0 || member.length > 0)) {
        const firstOrg = owned[0] || coOwned[0] || member[0];
        setActiveOrganization(firstOrg);
      }

    } catch (error) {
      console.error('Error fetching organizations:', error);
      if (error.errors) {
        console.error('GraphQL Errors:', error.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogoUrl = async (logoKey) => {
    try {
      if (!logoKey) return null;
      
      const url = await Storage.get(logoKey, {
        level: 'public',
        expires: 60 * 60 * 24 // 24 hours
      });
      return url;
    } catch (error) {
      console.error('Error fetching logo URL:', error);
      return null;
    }
  };

  const createOrganization = async (e) => {
    e.preventDefault();
    setError(null);
    setCreatingOrganization(true);

    // Shared implementation — see src/utils/createOrganization.js. This handler only maps the result
    // onto the modal's UI state (close + reset + reload on success; inline error otherwise).
    const result = await createOrganizationForUser(formData);
    if (!result.ok) {
      setError(result.error);
      setCreatingOrganization(false);
      return;
    }

    setShowCreateModal(false);
    setFormData({ name: '', contactEmail: '', contactPhone: '' });

    if (!result.templatesFailed) {
      setCreatingOrganization(false);
      // Reload so the new org is picked up as active across the app.
      setTimeout(() => window.location.reload(), 1000);
    } else {
      setCreatingOrganization(false);
      setError('⚠️ Organization created successfully, but template setup failed. You may need to set up templates manually.');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOrganizationSelect = (org) => {
    setActiveOrganization(org);
    navigate('/dashboard'); // Navigate to dashboard after selection
  };

  const hasManagementAccess = (organization) => {
    if (!organization || !user?.attributes?.sub || !user?.attributes?.email) return false;
    return (
      organization.owner === user.attributes.sub || 
      (Array.isArray(organization.additionalOwners) && organization.additionalOwners.includes(user.attributes.email))
    );
  };

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{dropdownStyles}</style>
      <Dropdown>
        <Dropdown.Toggle 
          variant="light" 
          className="d-flex align-items-center custom-dropdown-toggle" 
          style={{ 
            backgroundColor: '#00897b', 
            color: 'white',
            borderColor: '#00897b'
          }}>
          {activeOrganization ? (
            <>
              {activeOrgLogo ? (
                <Image 
                  src={activeOrgLogo} 
                  alt={activeOrganization.name}
                  className="me-2" 
                  width="24" 
                  height="24" 
                  roundedCircle 
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                <FontAwesomeIcon icon={faBuilding} className="me-2" />
              )}
              <span className="me-2">{activeOrganization.name}</span>
              {activeOrganization.owner === user?.attributes?.sub && (
                <Badge bg="warning" className="me-2" style={{ fontSize: '0.7em' }}>Owner</Badge>
              )}
              {activeOrganization.additionalOwners?.includes(user?.attributes?.email) && (
                <Badge bg="info" className="me-2" style={{ fontSize: '0.7em' }}>Co-Owner</Badge>
              )}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faBuilding} className="me-2" />
              <span className="me-2">Select Organization</span>
            </>
          )}
          <FontAwesomeIcon icon={faChevronDown} />
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ minWidth: '300px', padding: '0.5rem' }}>
          {organizations.owned.length > 0 && (
            <div className="mb-2">
              <Dropdown.Header className="d-flex align-items-center bg-warning bg-opacity-10 rounded py-2 px-2">
                <FontAwesomeIcon icon={faCrown} className="me-2 text-warning" />
                <span className="fw-bold">Organizations You Own</span>
              </Dropdown.Header>
              {organizations.owned.map(org => (
                <Dropdown.Item
                  key={org.id}
                  active={activeOrganization?.id === org.id}
                  onClick={() => handleOrganizationSelect(org)}
                  className="rounded mt-1"
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      {logoUrls[org.id] ? (
                        <Image 
                          src={logoUrls[org.id]} 
                          alt={org.name}
                          className="me-2" 
                          width="24" 
                          height="24" 
                          roundedCircle 
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                      )}
                      <span>{org.name}</span>
                    </div>
                    <Badge bg="warning" className="ms-2" style={{ fontSize: '0.7em' }}>Owner</Badge>
                  </div>
                </Dropdown.Item>
              ))}
            </div>
          )}

          {organizations.coOwned.length > 0 && (
            <div className="mb-2">
              <Dropdown.Header className="d-flex align-items-center bg-info bg-opacity-10 rounded py-2 px-2">
                <FontAwesomeIcon icon={faUserShield} className="me-2 text-info" />
                <span className="fw-bold">Organizations You Co-Own</span>
              </Dropdown.Header>
              {organizations.coOwned.map(org => (
                <Dropdown.Item
                  key={org.id}
                  active={activeOrganization?.id === org.id}
                  onClick={() => handleOrganizationSelect(org)}
                  className="rounded mt-1"
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      {logoUrls[org.id] ? (
                        <Image 
                          src={logoUrls[org.id]} 
                          alt={org.name}
                          className="me-2" 
                          width="24" 
                          height="24" 
                          roundedCircle 
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                      )}
                      <span>{org.name}</span>
                    </div>
                    <Badge bg="info" className="ms-2" style={{ fontSize: '0.7em' }}>Co-Owner</Badge>
                  </div>
                </Dropdown.Item>
              ))}
            </div>
          )}

          {organizations.member.length > 0 && (
            <div className="mb-2">
              <Dropdown.Header className="d-flex align-items-center bg-secondary bg-opacity-10 rounded py-2 px-2">
                <FontAwesomeIcon icon={faUsers} className="me-2 text-secondary" />
                <span className="fw-bold">Organizations You're A Member Of</span>
              </Dropdown.Header>
              {organizations.member.map(org => (
                <Dropdown.Item
                  key={org.id}
                  active={activeOrganization?.id === org.id}
                  onClick={() => handleOrganizationSelect(org)}
                  className="rounded mt-1"
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      {logoUrls[org.id] ? (
                        <Image 
                          src={logoUrls[org.id]} 
                          alt={org.name}
                          className="me-2" 
                          width="24" 
                          height="24" 
                          roundedCircle 
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                      )}
                      <span>{org.name}</span>
                    </div>
                    <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7em' }}>Member</Badge>
                  </div>
                </Dropdown.Item>
              ))}
            </div>
          )}

          <Dropdown.Divider />
          {activeOrganization && hasManagementAccess(activeOrganization) && (
            <>
              <Dropdown.Item 
                as={Link} 
                to="/organization-management"
                className="rounded"
              >
                <FontAwesomeIcon icon={faCog} className="me-2" />
                Manage Organization
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}
          <Dropdown.Item 
            onClick={() => setShowCreateModal(true)} 
            className="text-primary rounded"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create New Organization
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <Modal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {hasNoOrganizations() ? 'Create Your First Organization' : 'Create New Organization'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={createOrganization}>
            <Form.Group className="mb-3">
              <Form.Label>Organization Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contact Email *</Form.Label>
              <Form.Control
                type="email"
                value={formData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contact Phone *</Form.Label>
              <Form.Control
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              {hasNoOrganizations() ? (
                <Button 
                  variant="secondary" 
                  onClick={() => { setWaitingForInvite(true); setShowCreateModal(false); }}
                  disabled={creatingOrganization}
                >
                  I'm waiting for an invitation
                </Button>
              ) : (
                <Button 
                  variant="secondary" 
                  onClick={() => setShowCreateModal(false)}
                  disabled={creatingOrganization}
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" variant="primary" disabled={creatingOrganization}>
                {creatingOrganization ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Setting up organization...
                  </>
                ) : (
                  'Create Organization'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default OrganizationSelector; 